import type { RouteObject } from 'react-router';
import { RequerPermissao } from '@/app/RequerPermissao';
import { paginaSobDemanda } from '@/app/pagina-sob-demanda';

/**
 * Rotas do módulo Provedores, cada grupo sob `RequerPermissao` como **rota-mãe**.
 *
 * 🚨 Segunda tranca. A que vale é o `@Permissao()` de cada rota do backend: aqui só evitamos mostrar uma tela
 * que o servidor negaria.
 *
 * 🚨 **Rota estática antes da parametrizada.** `/provedores/operacoes` e `/provedores/novo` são declaradas
 * antes de `/provedores/:id`. O react-router pontua segmento estático acima de dinâmico e acertaria mesmo na
 * ordem trocada, mas a ordem explícita é o que se lê — e foi exatamente essa disputa que, no backend,
 * transformou `GET /provedores/operacoes` num 422 de UUID na iteração 14.3. Há teste afirmando a ordem.
 *
 * Iteração 14.1: o catálogo das operações de crédito, sob `provedores.ver` — manter exige
 * `provedores.operacoes`. Iteração 14.2: a página de uma operação, pelo código do guia. Iteração 14.4: o
 * diretório, o cadastro, a ficha e a edição do provedor. Iteração 14.6: a central de ofertas, a ficha de uma
 * oferta e os formulários dela. Iteração 14.11: o relatório da importação, sob `provedores.importar` — a
 * carga em si roda por script, no servidor. Iteração 14.13: o painel do acervo, sob `provedores.painel`.
 */
export const rotas: RouteObject[] = [
  {
    element: <RequerPermissao permissao="provedores.ver" />,
    children: [
      {
        path: '/provedores',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaProvedores'),
          (m) => m.PaginaProvedores,
          'Abrindo o diretório de provedores…',
        ),
      },
      {
        // Estática, antes de `/provedores/:id` — a mesma disputa de caminho das operações.
        path: '/provedores/ofertas',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaOfertas'),
          (m) => m.PaginaOfertas,
          'Abrindo a central de ofertas…',
        ),
      },
      {
        path: '/provedores/ofertas/:id',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaOfertaDetalhe'),
          (m) => m.PaginaOfertaDetalhe,
          'Abrindo a ficha da oferta…',
        ),
      },
      {
        path: '/provedores/operacoes',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaOperacoesDeCredito'),
          (m) => m.PaginaOperacoesDeCredito,
          'Abrindo as operações de crédito…',
        ),
      },
      {
        // 🚨 Depois da rota sem parâmetro, e com o código do guia como parâmetro (14.2): é ele que liga a
        // tela à seção do documento que a equipe lê, e é o que se cola de uma conversa.
        path: '/provedores/operacoes/:codigo',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaDaOperacao'),
          (m) => m.PaginaDaOperacao,
          'Abrindo a operação de crédito…',
        ),
      },
      {
        path: '/provedores/:id',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaProvedorDetalhe'),
          (m) => m.PaginaProvedorDetalhe,
          'Abrindo a ficha do provedor…',
        ),
      },
    ],
  },
  {
    // O painel: chave própria, porque mostra o acervo **agregado** — outra leitura que abrir uma ficha (14.13).
    element: <RequerPermissao permissao="provedores.painel" />,
    children: [
      {
        // Estática, antes de `/provedores/:id`, como as outras.
        path: '/provedores/painel',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaPainelProvedores'),
          (m) => m.PaginaPainelProvedores,
          'Abrindo o painel do acervo…',
        ),
      },
    ],
  },
  {
    // O relatório da carga: permissão própria, porque é tela de operação, não de consulta (14.11).
    element: <RequerPermissao permissao="provedores.importar" />,
    children: [
      {
        path: '/provedores/importacao',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaRelatorioImportacao'),
          (m) => m.PaginaRelatorioImportacao,
          'Abrindo o relatório da importação…',
        ),
      },
    ],
  },
  {
    // Cadastrar é permissão própria: quem só consulta não vê a tela nem a rota (ADR 0007).
    element: <RequerPermissao permissao="provedores.criar" />,
    children: [
      {
        path: '/provedores/novo',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaProvedorNovo'),
          (m) => m.PaginaProvedorNovo,
          'Abrindo o cadastro de provedor…',
        ),
      },
      {
        // A ficha nasce **dentro** de um provedor: é ele que a identifica, junto da operação.
        path: '/provedores/:id/ofertas/nova',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaOfertaNova'),
          (m) => m.PaginaOfertaNova,
          'Abrindo a nova oferta…',
        ),
      },
    ],
  },
  {
    element: <RequerPermissao permissao="provedores.editar" />,
    children: [
      {
        path: '/provedores/:id/editar',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaProvedorEditar'),
          (m) => m.PaginaProvedorEditar,
          'Abrindo a edição do provedor…',
        ),
      },
      {
        path: '/provedores/ofertas/:id/editar',
        element: paginaSobDemanda(
          () => import('./paginas/PaginaOfertaEditar'),
          (m) => m.PaginaOfertaEditar,
          'Abrindo a edição da ficha…',
        ),
      },
    ],
  },
];
